import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

import { useAiBulkNavigationGotoPage } from './providers/AiBulkNavigationContextProvider';
import { useBulkCreateContext } from './providers/BulkCreateContextProvider';
import { BulkCreateIssueCard } from './BulkCreateIssueCard';
import { JiraProject } from './__types__/apiUtils';
import { messages } from './messages';
import { AvailableSite } from '@atlassian/gic-anywhere';
import { BulkCreateErrorSectionMessage } from './BulkCreateErrorSectionMessage';

// Used for both AI and non-AI bulk create
export const BulkCreateBody = () => {
	const goToPage = useAiBulkNavigationGotoPage();
	const [{ aiBulkIssuesList, currentJiraSite, currentJiraProject }] = useBulkCreateContext();
	const onConfigureClick = useCallback(
		(event) => {
			event.preventDefault();
			void goToPage('configure');
		},
		[goToPage],
	);

	// This section is only for testing and should be updated once the Configure
	// view is built
	const [, { setCurrentJiraSite, setCurrentJiraProject }] = useBulkCreateContext();
	useEffect(() => {
		void setCurrentJiraSite({
			cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
			url: 'https://hello.atlassian.net',
			avatarUrl: 'https://site-admin-avatar-cdn.prod.public.atl-paas.net/avatars/240/koala.png',
			displayName: 'hello',
		} as AvailableSite);
		void setCurrentJiraProject({
			entityId: 'd00888b2-0aab-41bc-985f-a4d8e7800da5',
			expand: 'description,lead,issueTypes,url,projectKeys,permissions,insight',
			isPrivate: false,
			projectTypeKey: 'software',
			self: 'https://hello.atlassian.net/ex/jira/a436116f-02ce-4520-8fbb-7301462a1674/rest/api/3/project/31495',
			simplified: true,
			style: 'next-gen',
			uuid: 'd00888b2-0aab-41bc-985f-a4d8e7800da5',
			name: 'Atlas One',
			key: 'A0',
			id: '31495',
			avatarUrls: {
				'48x48':
					'https://hello.atlassian.net/ex/jira/a436116f-02ce-4520-8fbb-7301462a1674/rest/api/3/universal_avatar/view/type/project/avatar/35308',
				'24x24':
					'https://hello.atlassian.net/ex/jira/a436116f-02ce-4520-8fbb-7301462a1674/rest/api/3/universal_avatar/view/type/project/avatar/35308?size=small',
				'16x16':
					'https://hello.atlassian.net/ex/jira/a436116f-02ce-4520-8fbb-7301462a1674/rest/api/3/universal_avatar/view/type/project/avatar/35308?size=xsmall',
				'32x32':
					'https://hello.atlassian.net/ex/jira/a436116f-02ce-4520-8fbb-7301462a1674/rest/api/3/universal_avatar/view/type/project/avatar/35308?size=medium',
			},
		} as JiraProject);
	}, [setCurrentJiraSite, setCurrentJiraProject]);

	return (
		<Box padding="space.200" paddingBlockStart="space.100">
			<Stack space="space.200">
				<Stack space="space.100">
					<BulkCreateErrorSectionMessage />
					{currentJiraSite && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage {...messages.jiraSiteHeading} />
							</Text>
							<Text color="color.text.subtlest">{currentJiraSite.displayName}</Text>
						</Inline>
					)}
					{currentJiraProject && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage {...messages.jiraProjectHeading} />
							</Text>
							<Text color="color.text.subtlest">{currentJiraProject.name}</Text>
						</Inline>
					)}
					<Text size="small" weight="bold">
						{/* This is a valid use of <Link> as it is used for navigation */}
						{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
						<Link href="#" testId="bulk-create-configure-button" onClick={onConfigureClick}>
							Configure
						</Link>
					</Text>
				</Stack>
				<Stack space="space.100">
					{aiBulkIssuesList.map((issue) => (
						<BulkCreateIssueCard key={issue.id} issue={issue} />
					))}
				</Stack>
			</Stack>
		</Box>
	);
};
